import type {
  OrdemDasPessoas,
  RecorrenciaDaPessoa,
  SemanaDeEstudo,
} from '@titularis/api-client';

/**
 * O vocabulário da recorrência: se a pessoa voltou a estudar, e quando.
 *
 * **A régua é da API** (`recorrenciaDe` e `semanasDeEstudo`, no módulo
 * `people`): um dia de estudo é um dia de Brasília com resposta, resolução
 * aberta ou artigo de lei lido, e "voltou" é dois dias ou mais. Aqui só se
 * decide como dizer isso - a tela não reconta dia nenhum, e é por isso que o
 * filtro e a coluna não têm como discordar.
 *
 * Arquivo próprio, como o `listas.ts`: a recorrência aparece na coluna, no
 * filtro, na ordenação e na aba, e tudo o que ela diz mora aqui.
 */

/** Na ordem em que se procura: quem voltou primeiro. */
export const RECORRENCIAS: RecorrenciaDaPessoa[] = ['voltou', 'um_dia', 'nunca'];

export const RECORRENCIA_LABEL: Record<RecorrenciaDaPessoa, string> = {
  voltou: 'Voltou a estudar',
  um_dia: 'Estudou um dia só',
  nunca: 'Nunca estudou',
};

export const RECORRENCIA_AJUDA: Record<RecorrenciaDaPessoa, string> = {
  voltou:
    'Estudou em dois dias diferentes ou mais. Quantos, a coluna Recorrência diz - e ordenar por ela põe quem mais volta no topo.',
  um_dia:
    'Experimentou num dia e não voltou mais. É quem um lembrete ainda alcança, e quanto mais cedo melhor.',
  nunca:
    'Não respondeu questão, não abriu resolução nem leu artigo de lei. Inclui quem comprou e ainda não começou.',
};

/**
 * As ordens da lista, para o seletor do celular. No computador quem ordena é o
 * cabeçalho da tabela, e as duas coisas escrevem o mesmo `?ordem=`.
 */
export const ORDENS: OrdemDasPessoas[] = ['entrada', 'dias_de_estudo', 'ultimo_estudo'];

export const ORDEM_LABEL: Record<OrdemDasPessoas, string> = {
  entrada: 'Entrada mais recente',
  dias_de_estudo: 'Mais dias de estudo',
  ultimo_estudo: 'Estudou por último',
};

/** "5 dias de estudo", "1 dia de estudo", "nenhum dia de estudo". */
export function diasPorExtenso(dias: number): string {
  if (dias === 0) return 'nenhum dia de estudo';
  return dias === 1 ? '1 dia de estudo' : `${dias.toLocaleString('pt-BR')} dias de estudo`;
}

/**
 * `DD/MM` de um dia em `AAAA-MM-DD`.
 *
 * **Recortado do texto, e nunca passado por `Date`.** O dia já é de Brasília -
 * a API o calculou -, e um `new Date('2026-09-28')` é meia-noite em UTC, que
 * em Brasília é a noite do dia 27: a semana apareceria com a data do domingo.
 */
export function rotuloDoDia(dia: string): string {
  const [, mes, diaDoMes] = dia.split('-');
  return `${diaDoMes}/${mes}`;
}

/**
 * O dia `semanas` semanas depois deste, em `AAAA-MM-DD`. Aritmética de
 * calendário em UTC sobre um dia que já é de Brasília: nenhum fuso entra.
 */
export function somarSemanas(dia: string, semanas: number): string {
  const data = new Date(`${dia}T00:00:00Z`);
  data.setUTCDate(data.getUTCDate() + 7 * semanas);
  return data.toISOString().slice(0, 10);
}

/**
 * O quanto a célula da faixa se acende.
 *
 * Quatro degraus, e não uma cor por número de dias: uma semana de um dia e uma
 * de sete precisam se distinguir de relance, e sete tons de verde numa célula
 * de doze pixels não se distinguem. `antes` é a semana em que a conta ainda
 * não existia - nem estudo, nem ausência.
 */
export type Intensidade = 'antes' | 0 | 1 | 2 | 3;

export function intensidadeDaSemana(dias: number | null): Intensidade {
  if (dias === null) return 'antes';
  if (dias === 0) return 0;
  if (dias === 1) return 1;
  return dias <= 3 ? 2 : 3;
}

/** O balão de uma célula da faixa. */
export function descricaoDaSemana(semana: SemanaDeEstudo, atual: boolean): string {
  const quando = atual
    ? `Esta semana (desde ${rotuloDoDia(semana.inicio)})`
    : `Semana de ${rotuloDoDia(semana.inicio)}`;

  if (semana.dias === null) return `${quando}: a conta ainda não existia`;
  return `${quando}: ${diasPorExtenso(semana.dias)}`;
}

/**
 * Quanto de um total, de 0 a 1, para o `formatarTaxa`. Sem total não há
 * proporção, e "0%" afirmaria uma queda que ninguém mediu.
 */
export function proporcao(parte: number, total: number): number | null {
  return total === 0 ? null : parte / total;
}
